/**
 * ConnectedClients: the directory↔clients care web (Directory detail
 * rebuild §5). Renders on BOTH detail pages only for a viewer who reads
 * every client (canReadEveryClient, the roster's width: the same gate the
 * server routes enforce; the directory pages themselves are open to all
 * roles, so the CALLER decides whether to mount this at all). A denied
 * role fires no fetch and sees no section.
 *
 * Rows are name-only linked rows (the wire carries client_id, display_name,
 * is_active — no invented meta); each links to the client record. Archived
 * clients stay listed (the connection is a fact) and render muted with an
 * "Archived" mini-chip. Read-only projection — no edit/remove.
 */

import { type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { Chip } from '@/components/primitives/Chip';
import { DirectoryRow } from '@/components/directory/DirectoryRow';
import { Skeleton } from '@/components/Skeleton';

export type ConnectedClientsSubject =
  | { kind: 'individual'; id: string }
  | { kind: 'organization'; id: string };

export interface ConnectedClientsProps {
  tenantSlug: string;
  subject: ConnectedClientsSubject;
  /** The subject's name — frames the empty-state copy. */
  name: string;
}

export function ConnectedClients({ tenantSlug, subject, name }: ConnectedClientsProps): ReactNode {
  const isOrg = subject.kind === 'organization';
  const noun = isOrg ? 'organization' : 'contact';
  const query = useQuery({
    queryKey: ['connected-clients', subject.kind, tenantSlug, subject.id],
    queryFn: ({ signal }) =>
      isOrg
        ? api.getConnectedClientsForOrg(tenantSlug, subject.id, signal)
        : api.getConnectedClients(tenantSlug, subject.id, signal),
  });
  const clients = query.data?.clients ?? [];

  return (
    <section className="rounded-card border border-sage-800/[8%] bg-paper p-4 shadow-sh1 sm:p-5">
      <div className="mb-2 flex items-center gap-2">
        <h2 className="font-display text-lg text-sage-800">Connected clients</h2>
        {clients.length > 0 && (
          <span className="rounded-pill bg-sage-50 px-2 py-0.5 text-xs font-semibold text-sage-400">
            {clients.length}
          </span>
        )}
      </div>

      <div data-testid="connected-clients">
        {query.isLoading && <Skeleton width="w-1/2" height="h-4" />}
        {query.isError && (
          <p className="text-sm text-ink-soft">Connected clients aren’t available right now.</p>
        )}
        {query.data &&
          (clients.length === 0 ? (
            <p className="text-sm italic text-ink-soft">
              No clients are connected to {name} yet. They appear here when a care team links this{' '}
              {noun}.
            </p>
          ) : (
            <div className="divide-y divide-sage-800/[8%]">
              {clients.map((c) => (
                <DirectoryRow
                  key={c.client_id}
                  testId="connected-client-row"
                  to={`/t/${tenantSlug}/clients/${c.client_id}`}
                  avatar="client"
                  name={c.display_name}
                  muted={!c.is_active}
                  trailing={
                    !c.is_active ? <Chip variant="neutral">Archived</Chip> : undefined
                  }
                />
              ))}
            </div>
          ))}
      </div>

      {clients.length > 0 && (
        <p className="mt-3 text-xs text-ink-soft">
          Clients appear here automatically when a care team links this {noun} to their record.
        </p>
      )}
    </section>
  );
}
